import React, { Ref, useMemo } from 'react';
import { closestCenter, DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils/cn';
import { DragEvent, StanDraggableItem, StanDraggableZoneProps } from './StanDraggable.types';
import { useStanDraggableContext } from './StanDraggable.context';
import { restrictToHorizontalAxis, restrictToVerticalAxis } from './StanDraggable.modifiers';
import styles from './StanDraggable.module.css';

interface StanDraggableZonePropsWithRef extends StanDraggableZoneProps {
    ref?: Ref<HTMLDivElement>;
}

export const StanDraggableZone = ({
    children,
    className = '',
    direction = 'vertical',
    gap = 'sm',
    disabled = false,
    ref,
}: StanDraggableZonePropsWithRef) => {
    const context = useStanDraggableContext();
    const { onReorder, onDragStart, onDragEnd } = context;

    // Extract draggable item IDs from children
    const draggableItemIds = useMemo(() => {
        const ids: string[] = [];
        React.Children.forEach(children, (child, index) => {
            if (React.isValidElement<{ id?: string }>(child) && child.props && 'id' in child.props) {
                ids.push((child.props.id as string) || `item-${index}`);
            }
        });
        return ids;
    }, [children]);

    const sortingStrategy = direction === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy;

    const axisModifier = direction === 'horizontal' ? restrictToHorizontalAxis : restrictToVerticalAxis;
    const modifiers = [axisModifier];

    const handleDragStart = (event: DragStartEvent) => {
        if (disabled) return;

        const { active } = event;
        const oldIndex = draggableItemIds.findIndex(id => id === active.id);

        if (oldIndex !== -1) {
            // Create a simple item representation for the event
            const item: StanDraggableItem = {
                id: active.id as string,
                content: null, // We don't need the actual content for the event
                disabled: false,
                data: {},
            };

            const dragEvent: DragEvent = {
                item,
                oldIndex,
                newIndex: oldIndex, // For drag start, newIndex is same as oldIndex
                allItems: draggableItemIds.map(id => ({ id, content: null, disabled: false, data: {} })),
            };
            onDragStart?.(dragEvent);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (disabled) return;

        const { active, over } = event;
        const oldIndex = draggableItemIds.findIndex(id => id === active.id);

        if (oldIndex !== -1) {
            const item: StanDraggableItem = {
                id: active.id as string,
                content: null,
                disabled: false,
                data: {},
            };

            let newIndex = oldIndex; // Default to oldIndex if no reorder happened

            if (over && active.id !== over.id) {
                const targetIndex = draggableItemIds.findIndex(id => id === over.id);
                if (targetIndex !== -1) {
                    newIndex = targetIndex;
                }
            }

            // Create a new order array based on the reorder
            const newOrder = [...draggableItemIds];
            if (oldIndex !== newIndex) {
                const [movedId] = newOrder.splice(oldIndex, 1);
                if (movedId) {
                    newOrder.splice(newIndex, 0, movedId);
                }
            }

            const dragEvent: DragEvent = {
                item,
                oldIndex,
                newIndex,
                allItems: newOrder.map(id => ({ id, content: null, disabled: false, data: {} })),
            };

            // Only call onReorder if there was an actual reorder
            if (oldIndex !== newIndex) {
                onReorder(dragEvent);
            }

            onDragEnd?.(dragEvent);
        }
    };

    const zoneClasses = cn(
        styles.zone,
        {
            [styles.horizontal as string]: direction === 'horizontal',
            [styles['gap-none'] as string]: gap === 'none',
            [styles['gap-sm'] as string]: gap === 'sm',
            [styles['gap-md'] as string]: gap === 'md',
            [styles['gap-lg'] as string]: gap === 'lg',
        },
        className
    );

    return (
        <DndContext
            collisionDetection={closestCenter}
            modifiers={modifiers}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={draggableItemIds} strategy={sortingStrategy}>
                <div ref={ref} className={zoneClasses}>
                    {children}
                </div>
            </SortableContext>
        </DndContext>
    );
};

StanDraggableZone.displayName = 'StanDraggableZone';
